import { useEffect, useState } from "react";
import {
  AdminSensor,
  listSensors,
  sensorKindLabel,
  sensorVersionLabel,
  updateSensor,
} from "../../../Services/Admin/Sensors";
import DeleteSensorButton from "./DeleteSensorButton";
import VenuePicker from "./VenuePicker";
import { useVenueOptions } from "./useVenueOptions";
import "./SensorsPanel.css";

function timeAgo(iso: string | null) {
  if (!iso) return "never";
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

export default function UnclaimedSensorsPanel() {
  const [sensors, setSensors] = useState<AdminSensor[]>([]);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const venueOptions = useVenueOptions();

  const loadSensors = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSensors();
      setSensors(data.items.filter((sensor) => sensor.claimStatus === "UNCLAIMED"));
    } catch (e: any) {
      setError(e?.message ?? "Could not load unclaimed sensors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSensors();
  }, []);

  const claim = async (sensor: AdminSensor) => {
    const venueId = selected[sensor.sensorId];
    if (!venueId) return;
    setBusyId(sensor.sensorId);
    setError(null);
    setNotice(null);
    try {
      await updateSensor(sensor.sensorId, { venueId });
      setSensors((items) => items.filter((item) => item.sensorId !== sensor.sensorId));
      setNotice(
        `${sensor.installationName ?? sensor.sensorId} is assigned and now appears in the connected fleet.`
      );
    } catch (e: any) {
      setError(e?.message ?? "Could not assign the sensor");
    } finally {
      setBusyId(null);
    }
  };

  const onDeleted = ({
    sensorId,
    credentialRevoked,
  }: {
    sensorId: string;
    credentialRevoked: boolean;
  }) => {
    setSensors((items) => items.filter((item) => item.sensorId !== sensorId));
    setNotice(
      credentialRevoked
        ? `${sensorId} was deleted and its credential was revoked.`
        : `${sensorId} was deleted. It will return here if it reports in again.`
    );
  };

  return (
    <div className="admin-panel">
      <div className="admin-toolbar">
        <button type="button" onClick={loadSensors} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
        <input
          className="admin-toolbar-grow"
          value={venueOptions.query}
          onChange={(event) => venueOptions.setQuery(event.target.value)}
          placeholder="Search venues, e.g. Pustervik"
          aria-label="Search venues"
        />
      </div>

      {(error || venueOptions.error) && (
        <div className="admin-error">{error ?? venueOptions.error}</div>
      )}
      {notice && <div className="admin-notice">{notice}</div>}
      {!loading && sensors.length === 0 && (
        <p className="admin-empty">No authenticated sensors are waiting for assignment.</p>
      )}

      {sensors.map((sensor) => {
        const busy = busyId === sensor.sensorId;
        return (
          <article className="sensor-card sensor-card--unclaimed" key={sensor.sensorId}>
            <div className="sensor-head">
              <div className="sensor-head-main">
                <strong>{sensor.installationName ?? sensor.sensorId}</strong>
                <div className="sensor-presence">
                  <span className={sensor.online ? "sensor-online" : "sensor-offline"}>
                    {sensor.online ? "Online" : "Offline"}
                  </span>
                  <span>· last seen {timeAgo(sensor.lastSeenAt)}</span>
                </div>
                <div className="sensor-dim">
                  {sensorKindLabel(sensor)} · {sensorVersionLabel(sensor)}
                </div>
                <div className="sensor-dim sensor-id">ID {sensor.sensorId}</div>
              </div>
              <span className="sensor-claim-badge">Unclaimed</span>
            </div>

            <div className="sensor-claim-row">
              <VenuePicker
                current={null}
                value={selected[sensor.sensorId] ?? ""}
                options={venueOptions.options}
                disabled={busy}
                label="Assign to venue"
                onChange={(venueId) =>
                  setSelected((current) => ({
                    ...current,
                    [sensor.sensorId]: venueId,
                  }))
                }
              />
              <button
                type="button"
                className="admin-btn--primary"
                disabled={busy || !selected[sensor.sensorId]}
                onClick={() => claim(sensor)}
              >
                {busy ? "Assigning…" : "Assign to venue"}
              </button>
            </div>

            <div className="sensor-danger">
              <DeleteSensorButton
                sensorId={sensor.sensorId}
                name={sensor.installationName}
                disabled={busy}
                onDeleted={onDeleted}
                onError={setError}
              />
            </div>
          </article>
        );
      })}
    </div>
  );
}
